import React from "react";
import { connect } from "react-redux";
import {
    currentOrganization,
    userOrganizations
} from "./selectors/user.selector";
import { currentSalesOrderId } from "./selectors/data.selector";
import EntityActions from "./actions";
import EntitySelectors from "./selectors";
import User from "./actions/user.actions";
import UserActions from "../redux/actions/user.actions";

let salesOrderActions = EntityActions.salesOrder;
let syncActions = EntityActions.sync;
let syncSelectors = EntitySelectors.sync;

export function withUrlSync(ComposedComponent) {
    class ReduxUrlSync extends React.Component {
        // True while await setCurrentOrganization is in flight (SSO gate may be async).
        _orgSwitchInFlight = false;
        // URL/orgs changed during in-flight switch — re-run sync once it finishes.
        _pendingUrlSync = false;
        // URL orgId we already SSO-denied this visit (avoid deny/retry loop).
        _ssoDeniedUrlOrgId = null;

        componentDidMount() {
            this._syncUrlToRedux(this.props);
        }

        componentDidUpdate(prevProps) {
            const prevParams = prevProps.match.params;
            const nextParams = this.props.match.params;
            // Only react to route identity changes, not every Redux invalidateAll.
            const routeParamsChanged =
                prevParams.orgId !== nextParams.orgId ||
                prevParams.salesOrderId !== nextParams.salesOrderId ||
                prevParams.syncId !== nextParams.syncId;

            if (routeParamsChanged) {
                this._ssoDeniedUrlOrgId = null;
            }

            // Deep link: mount often happens before userOrgs are loaded.
            const userOrgsBecameReady =
                !(prevProps.userOrgs && prevProps.userOrgs.length) &&
                this.props.userOrgs &&
                this.props.userOrgs.length > 0;

            // Do not sync on currentOrgIdChanged — menu apply before history.push
            // would see old URL orgId and switch Redux back to the previous tenant.
            if (routeParamsChanged || userOrgsBecameReady) {
                this._syncUrlToRedux(this.props);
            }
        }

        // Align Redux current org / SO / sync with URL. Moved out of mapStateToProps
        // so async SSO setCurrentOrganization cannot re-enter on every state update.
        async _syncUrlToRedux(props) {
            const { orgId, salesOrderId, syncId } = props.match.params;
            const { userOrgs, currentOrg, reduxSalesOrderId, reduxSyncId } =
                props;

            if (!orgId || !userOrgs) return;

            if (userOrgs && userOrgs.length == 0) {
                UserActions.signOut();
                return;
            }

            if (!isNaN(orgId) && (!currentOrg || currentOrg.id != orgId)) {
                if (this._ssoDeniedUrlOrgId == String(orgId)) {
                    return;
                }

                if (this._orgSwitchInFlight) {
                    this._pendingUrlSync = true;
                    return;
                }

                let org = userOrgs.find((o) => o.id == orgId) || userOrgs[0];
                if (org) {
                    this._orgSwitchInFlight = true;
                    try {
                        const orgSwitchAllowed =
                            await User.setCurrentOrganization(org);
                        if (!orgSwitchAllowed) {
                            this._ssoDeniedUrlOrgId = String(orgId);
                            return;
                        }
                        this._ssoDeniedUrlOrgId = null;

                        if (orgId && org.id != orgId) {
                            // this should never happen unless someone tries to give a orgId in the url
                            // which he doesn't have access to.
                            // Redirecting them to the org on which they have access.

                            if (window.location.pathname != `/${org.id}`) {
                                props.history.push("/");
                            }
                            //note that make sure you dont get into a loop here
                        }
                    } finally {
                        this._orgSwitchInFlight = false;
                        if (this._pendingUrlSync) {
                            this._pendingUrlSync = false;
                            this._syncUrlToRedux(this.props);
                        }
                    }
                } else {
                    // no org loaded for user yet.
                    UserActions.signOut();
                }
            }

            if (salesOrderId) {
                if (!reduxSalesOrderId || salesOrderId != reduxSalesOrderId) {
                    salesOrderActions.setCurrentId(salesOrderId);
                }
            }

            if (syncId) {
                if (!reduxSyncId || syncId != reduxSyncId) {
                    syncActions.setCurrentId(syncId);
                }
            }
        }

        render() {
            return (
                <>
                    <ComposedComponent {...this.props} />
                </>
            );
        }
    }

    // Pure — no setCurrentOrganization here (that caused the request storm).
    function mapStateToProps(state) {
        return {
            currentOrg: currentOrganization(state),
            userOrgs: userOrganizations(state),
            reduxSalesOrderId: currentSalesOrderId(state),
            reduxSyncId: syncSelectors.currentId(state)
        };
    }

    return connect(mapStateToProps)(ReduxUrlSync);
}
